import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { CircleCheck, CircleAlert, UserPlusIcon, ArrowLeftIcon } from 'lucide-react';

import { Main } from 'src/components/Layout';
import { usePageMeta, useIntegrationAuth } from 'src/hooks';
import { EmptyBlock } from '@/components/ui/empty-block';
import { Button } from '@/components/ui/button';
import { Loader } from 'src/components/Loader';
import { Card, CardContent } from '@/components/ui/card';
import { getProviderDisplayName } from 'src/utils/getProviderDisplayName';
import env from 'env';

import type { OauthIntegrationProviderEnum } from 'src/graphql/schema';

const Container = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [integrationId, setIntegrationId] = useState<string | null>(null);

  const { provider } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { finishAuth } = useIntegrationAuth(provider as OauthIntegrationProviderEnum);

  useEffect(() => {
    const onLoad = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code || !state) {
        setError('Invalid redirect url.');
        setLoading(false);
      } else {
        try {
          const { finishOauthIntegrationAuth } = await finishAuth(code, state);
          setIntegrationId(finishOauthIntegrationAuth?.oauthIntegration?.id ?? '');
        } catch (e) {
          if (e instanceof Error) setError(e.message);
        } finally {
          setLoading(false);
        }
      }
    };

    onLoad();
  }, []);

  if (loading) return <Loader />;

  if (error)
    return (
      <EmptyBlock
        icon={CircleAlert}
        title={`Couldn't connect to ${getProviderDisplayName(provider)}`}
        description={
          <>
            We couldn&apos;t set up the integration. Check the error details below and try again, or{' '}
            <a href={`mailto:${env.CODEKEEPER_SERVICE_EMAIL}`} className="underline">
              contact support
            </a>{' '}
            if the problem persists.
          </>
        }
        action={
          <div className="flex flex-col items-center gap-6">
            <Card className="bg-muted border-muted md:px-6 md:py-2">
              <CardContent className="p-2 text-sm">{error}</CardContent>
            </Card>
            <Button variant="secondary" size="sm" onClick={() => navigate('/integrations')} className="w-full">
              <ArrowLeftIcon /> Back to integrations
            </Button>
          </div>
        }
        classes={{
          title: 'font-bold mb-6',
          content: 'border-solid shadow-lg',
        }}
        withCard={false}
      />
    );

  const onShareAccess = () => navigate(`/integrations/${integrationId}`, { state: { openShareAccess: true } });

  return (
    <EmptyBlock
      icon={CircleCheck}
      title={`${getProviderDisplayName(provider)} is now connected`}
      description={`You can now select repositories from ${getProviderDisplayName(provider)} and deposit them into your escrow or Software Backup.\n\nShare access with team members so they can use this integration in escrows they are members of.`}
      action={
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-6">
          <Button variant="secondary" size="sm" onClick={() => navigate('/integrations')} className="sm:flex-1">
            <ArrowLeftIcon /> Back to integrations
          </Button>
          <Button size="sm" onClick={onShareAccess} className="sm:flex-1">
            <UserPlusIcon /> Share access
          </Button>
        </div>
      }
      classes={{
        title: 'font-bold mb-6',
        content: 'border-solid shadow-lg',
      }}
      withCard={false}
    />
  );
};

const IntegrationAuthPage = () => {
  usePageMeta('Integrations');

  return (
    <Main className="bg-background-secondary" title="Integrations">
      <div className="max-w-lg mx-auto">
        <Container />
      </div>
    </Main>
  );
};

export default IntegrationAuthPage;
